import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    AccordionTriggerRadiusConstantType,
    AccordionMeasuresConstantType,
    AccordionSurfaceConstantType,
    AccordionColorsConstantType,
    AccordionDynamicStyleType,
    AccordionTriggerRadius,
    AccordionMeasures,
    AccordionColors,
    AccordionSize
} from "./type";
import type {
    Mutable
} from "../../types";
import {
    getControlMeasures
} from "../../helpers/theme/control";
import {
    getSurfaceStyle
} from "../../helpers/theme/elevation";
import {
    concentric
} from "../../helpers/theme/concentric";
import {
    getCornerStyle
} from "../../helpers/theme/corner";

export const ACCORDION_CONTENT_SPACES: Record<AccordionSize, keyof NCoreUIKit.Spaces> = {
    medium: "spacingMd",
    large: "spacingLg",
    small: "spacingSm"
};

export const ACCORDION_SECONDARY_VARIANTS: Record<AccordionSize, keyof NCoreUIKit.Typography> = {
    medium: "bodyMediumSize",
    large: "bodyMediumSize",
    small: "bodySmallSize"
};

export const getAccordionMeasures = ({
    controlPaddings,
    controlHeights,
    shapeConfigs,
    typography,
    iconSizes,
    variant,
    spaces,
    size
}: AccordionMeasuresConstantType): AccordionMeasures => {
    const currentControl = getControlMeasures({
        controlPaddings,
        controlHeights,
        shapeConfigs,
        iconSizes,
        size
    });

    const lineHeight = typography[currentControl.labelVariant].lineHeight;

    return {
        paddingHorizontal: variant === "plain" ? 0 : currentControl.paddingHorizontal,
        paddingVertical: (currentControl.minHeight - lineHeight) / 2,
        contentPaddingBottom: spaces[ACCORDION_CONTENT_SPACES[size]],
        subTitleVariant: ACCORDION_SECONDARY_VARIANTS[size],
        labelVariant: currentControl.labelVariant,
        valueVariant: currentControl.valueVariant,
        minHeight: currentControl.minHeight,
        iconSize: currentControl.iconSize,
        gap: controlPaddings.small,
        lineHeight
    };
};

export const getAccordionColors = ({
    isDisabled,
    colors
}: AccordionColorsConstantType): AccordionColors => {
    if(isDisabled) {
        const disabledColor = colors.system.state.content.disabled.neutral;

        return {
            subTitleColor: disabledColor,
            chevronColor: disabledColor,
            titleColor: disabledColor,
            iconColorKey: "disabled",
            metaColor: disabledColor,
            iconColor: disabledColor
        };
    }

    return {
        subTitleColor: colors.content.text.mid,
        chevronColor: colors.content.icon.mid,
        titleColor: colors.content.text.high,
        iconColor: colors.content.icon.mid,
        metaColor: colors.content.text.low,
        iconColorKey: "mid"
    };
};

export const getAccordionSurfaceStyle = ({
    shadowAlpha,
    elevations,
    isDisabled,
    borders,
    colors
}: AccordionSurfaceConstantType): ViewStyle => {
    if(isDisabled) {
        return {
            backgroundColor: colors.system.state.overlay.disabled.neutral,
            borderColor: colors.system.state.border.disabled.neutral,
            borderWidth: borders.line
        };
    }

    return getSurfaceStyle({
        surface: "card",
        shadowAlpha,
        elevations,
        borders,
        colors
    });
};

export const getAccordionTriggerRadius = ({
    radiuses,
    borders,
    variant,
    isOpen,
    layout
}: AccordionTriggerRadiusConstantType): AccordionTriggerRadius => {
    const surfaceRadius = concentric({
        outer: radiuses.surface,
        role: radiuses.surface,
        inset: borders.line
    });

    if(variant === "card") {
        return {
            bottom: isOpen ? 0 : surfaceRadius,
            top: surfaceRadius
        };
    }

    return {
        bottom: layout.isLast && !isOpen ? surfaceRadius : 0,
        top: layout.isFirst ? surfaceRadius : 0
    };
};

const stylesheet = StyleSheet.create({
    container: {
        flexDirection: "column",
        borderStyle: "solid",
        minWidth: 0
    }
});

export const useStyles = ({
    shapeConfigs,
    shadowAlpha,
    elevations,
    radiuses,
    borders,
    variant,
    colors,
    spaces
}: AccordionDynamicStyleType) => {
    const styles = {
        container: {} as Mutable<ViewStyle>
    };

    if(variant === "card") {
        styles.container.gap = spaces.spacingSm;
    }

    if(variant === "contained") {
        Object.assign(styles.container, getAccordionSurfaceStyle({
            isDisabled: false,
            shadowAlpha,
            elevations,
            borders,
            colors
        }), getCornerStyle({
            radius: radiuses.surface,
            shapeConfigs
        }));
    }

    return styles;
};
export default stylesheet;
